import { useEffect, useState, useCallback } from "react";
import {
  Plus,
  Sparkles,
  Bot,
  Trash2,
  Pencil,
  CheckCircle2,
  AlertTriangle,
  Copy,
} from "lucide-react";
import PageHeader from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import AddAiModelDialog from "../components/AddAiModelDialog";
import TestPromptDialog from "../components/TestPromptDialog";
import { api, apiErrorMessage } from "../lib/api";
import { toast } from "sonner";
import { TID } from "../lib/testIds";

export default function AiModelsPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [testing, setTesting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/ai/models");
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(apiErrorMessage(e));
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function del(id) {
    if (!window.confirm(`¿Eliminar el modelo "${id}"?`)) return;
    try {
      await api.delete(`/ai/models/${encodeURIComponent(id)}`);
      toast.success("Modelo eliminado");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  function copyEndpoint(id) {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/ai/models/${id}/prompt`;
    navigator.clipboard.writeText(url).then(() => toast.success("Endpoint copiado"));
  }

  return (
    <div data-testid={TID.aiModels.root}>
      <PageHeader
        eyebrow="IA"
        title="Modelos IA"
        description="Chats web (Gemini, ChatGPT…) accesibles como un endpoint HTTP. Tus apps mandan prompts a KRAI API y reciben respuestas."
        actions={
          <Button
            data-testid={TID.aiModels.addButton}
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            className="bg-white text-black hover:bg-zinc-200 h-9"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Nuevo modelo
          </Button>
        }
      />

      <div className="mb-6 rounded-md border border-zinc-800 bg-zinc-950 p-4 flex items-start gap-3 text-xs">
        <Sparkles className="h-4 w-4 text-zinc-400 mt-0.5 shrink-0" />
        <div className="text-zinc-400 leading-relaxed">
          Cada modelo necesita un <b className="text-zinc-200">perfil</b> creado
          previamente en <b>Navegador / Playwright</b> (login manual una vez) o en{" "}
          <b>Sesiones</b> (login automático). Después, tus apps pueden usar:
          <code className="ml-1 font-mono text-zinc-300 bg-zinc-900 px-1.5 py-0.5 rounded">
            POST /api/ai/models/{"{id}"}/prompt
          </code>
        </div>
      </div>

      {loading ? (
        <div className="rounded-md border border-zinc-800 bg-zinc-950 p-10 text-sm text-zinc-500">
          Cargando modelos…
        </div>
      ) : error ? (
        <div className="rounded-md border border-red-900/40 bg-red-950/20 text-red-300 text-xs p-3">
          {error}
        </div>
      ) : list.length === 0 ? (
        <div className="rounded-md border border-dashed border-zinc-800 bg-zinc-950 p-10 text-sm text-zinc-500">
          Aún no hay modelos IA. Crea uno con "Nuevo modelo" (hay presets Gemini y ChatGPT).
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((m) => (
            <ModelCard
              key={m.id}
              model={m}
              onTest={() => setTesting(m)}
              onEdit={() => { setEditing(m); setDialogOpen(true); }}
              onDelete={() => del(m.id)}
              onCopy={() => copyEndpoint(m.id)}
            />
          ))}
        </div>
      )}

      <AddAiModelDialog
        key={editing?.id || "new"}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        onSaved={load}
      />
      <TestPromptDialog
        open={Boolean(testing)}
        onOpenChange={(v) => !v && setTesting(null)}
        model={testing}
      />
    </div>
  );
}

function ModelCard({ model, onTest, onEdit, onDelete, onCopy }) {
  return (
    <div
      data-testid={TID.aiModels.card(model.id)}
      className="rounded-md border border-zinc-800 bg-zinc-950 flex flex-col overflow-hidden hover:border-zinc-700 transition-colors duration-150"
    >
      <div className="p-5 flex items-start justify-between gap-3">
        <div className="min-w-0 flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-md border border-zinc-800 bg-zinc-900 grid place-items-center">
            <Bot className="h-4 w-4 text-zinc-300" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white truncate">{model.name}</div>
            <div className="text-[11px] font-mono text-zinc-500">id: {model.id}</div>
          </div>
        </div>
        {model.profile_ready ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400 shrink-0">
            <CheckCircle2 className="h-3 w-3" /> perfil
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] text-amber-400 shrink-0">
            <AlertTriangle className="h-3 w-3" /> sin perfil
          </span>
        )}
      </div>

      <div className="px-5 pb-4 space-y-3">
        <MetaRow label="URL">
          <a href={model.url} target="_blank" rel="noreferrer"
             className="font-mono text-xs text-zinc-300 hover:text-white truncate block">
            {model.url}
          </a>
        </MetaRow>
        <div className="grid grid-cols-2 gap-3">
          <MetaRow label="Perfil">
            <span className="font-mono text-xs text-zinc-300 truncate block">
              {model.profile} <span className="text-zinc-600">({model.profile_type})</span>
            </span>
          </MetaRow>
          <MetaRow label="Endpoint">
            <button
              onClick={onCopy}
              className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white"
              title="Copiar URL del endpoint"
            >
              <Copy className="h-3 w-3" /> copiar
            </button>
          </MetaRow>
        </div>
      </div>

      <div className="mt-auto border-t border-zinc-800 p-3 flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          data-testid={TID.aiModels.test(model.id)}
          onClick={onTest}
          className="bg-white text-black hover:bg-zinc-200 h-8"
        >
          <Bot className="h-3.5 w-3.5 mr-1.5" /> Probar prompt
        </Button>
        <Button
          size="sm"
          variant="secondary"
          data-testid={TID.aiModels.edit(model.id)}
          onClick={onEdit}
          className="h-8 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <button
          data-testid={TID.aiModels.delete(model.id)}
          onClick={onDelete}
          className="ml-auto h-8 w-8 grid place-items-center rounded-md text-zinc-500 hover:text-red-400 hover:bg-zinc-900 transition-colors duration-150"
          title="Eliminar"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function MetaRow({ label, children }) {
  return (
    <div className="space-y-1 min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
        {label}
      </div>
      {children}
    </div>
  );
}
